import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { TemplateFile } from "../../../models/Templates";
import { Event, EventGuestStatistic, LiquidVariables } from "../../../models/Event";
import { LiquidValidator } from "../../../utils/LiquidValidator";

export type FieldMappings = Record<string, string>;

export interface FieldValidation {
    isValid: boolean;
    errorMessage?: string;
    warningMessage?: string;
}

export interface StepValidation {
    canNavigateTo: boolean;
    canProceedFrom: boolean;
    fieldValidations: Record<string, FieldValidation>;
}

export interface OutputSettings {
    documentName: string | null;
    guestFilter: string;
    documentNaming: "guest_uuid" | "guest_field";
    customFieldDefinitionId: string | null;
    customFieldOverwrite: boolean;
    deletionRule: "NONE" | "1WEEK" | "2WEEK" | "1MONTH" | "3MONTHS" | "6MONTHS";
    deleteDateFieldDefinitionId: string | null;
}

export const DEFAULT_DELETION_RULE = "6MONTHS";
export const TRIAL_DELETION_RULE = "1WEEK";

export interface WizardState {
    currentStep: number;
    selectedEvent: Event | null;
    selectedTemplate: TemplateFile | null;
    fieldMappings: FieldMappings;
    qrCodeOverrides: Record<string, string> | null;
    outputSettings: OutputSettings;
    liquidVariables: LiquidVariables | null;
    eventGuestStatistic: EventGuestStatistic | null;
    isComplete: boolean;
    validationTriggered: Record<number, boolean>;
}

export const WIZARD_STEPS = {
    SELECT_EVENT: 1,
    SELECT_TEMPLATE: 2,
    MAP_FIELDS: 3,
    OUTPUT_SETTINGS: 4,
    REVIEW: 5,
};

function buildEmptyWizardState(): WizardState {
    return {
        currentStep: WIZARD_STEPS.SELECT_EVENT,
        selectedEvent: null,
        selectedTemplate: null,
        fieldMappings: {},
        qrCodeOverrides: null,
        outputSettings: {
            documentName: null,
            guestFilter: "ALL",
            documentNaming: "guest_field", // 'guest_uuid' or 'guest_field'
            customFieldDefinitionId: null,
            customFieldOverwrite: false,
            deletionRule: DEFAULT_DELETION_RULE,
            deleteDateFieldDefinitionId: null,
        },
        liquidVariables: null,
        eventGuestStatistic: null,
        isComplete: false,
        validationTriggered: {},
    };
}

export const useCreateWizard = () => {
    const [wizardState, setWizardState] = useState<WizardState>(buildEmptyWizardState());
    const { t } = useTranslation("wizard");

    const updateWizardState = useCallback((updates: Partial<WizardState>) => {
        setWizardState((prev) => ({
            ...prev,
            ...updates,
        }));
    }, []);

    const setSelectedEvent = useCallback(
        (event: Event | null) => {
            updateWizardState({
                selectedEvent: event,
                liquidVariables: null,
                eventGuestStatistic: null,
            });
        },
        [updateWizardState],
    );

    const setSelectedTemplate = useCallback(
        (template: TemplateFile | null) => {
            updateWizardState({
                selectedTemplate: template,
                fieldMappings: {}, // Reset field mappings when template changes
                qrCodeOverrides: null, // Reset QR code overrides when template changes
            });
        },
        [updateWizardState],
    );

    const setFieldMappings = useCallback(
        (mappings: FieldMappings) => {
            updateWizardState({
                fieldMappings: mappings,
            });
        },
        [updateWizardState],
    );

    const setQrCodeOverrides = useCallback(
        (overrides: Record<string, string> | null) => {
            updateWizardState({
                qrCodeOverrides: overrides,
            });
        },
        [updateWizardState],
    );

    const setOutputSettings = useCallback(
        (settings: Partial<OutputSettings>) => {
            updateWizardState({
                outputSettings: { ...wizardState.outputSettings, ...settings },
            });
        },
        [updateWizardState, wizardState.outputSettings],
    );

    const setLiquidVariables = useCallback(
        (variables: LiquidVariables | null) => {
            updateWizardState({
                liquidVariables: variables,
            });
        },
        [updateWizardState],
    );

    const setEventGuestStatistic = useCallback(
        (statistic: EventGuestStatistic | null) => {
            updateWizardState({
                eventGuestStatistic: statistic,
            });
        },
        [updateWizardState],
    );

    const goToStep = useCallback(
        (step: number) => {
            updateWizardState({
                currentStep: step,
            });
        },
        [updateWizardState],
    );

    const validateCustomField = useCallback(
        (documentNaming: "guest_uuid" | "guest_field", customFieldDefinitionId: string | null): FieldValidation => {
            if (documentNaming === "guest_field" && !customFieldDefinitionId) {
                return {
                    isValid: false,
                    errorMessage: t("validation.customFieldRequired"),
                };
            }
            return { isValid: true };
        },
        [t],
    );

    // Validation functions
    const validateDocumentName = useCallback(
        (name: string | null): FieldValidation => {
            if (!name || name.trim().length === 0) {
                return {
                    isValid: false,
                    errorMessage: t("validation.documentNameRequired"),
                };
            }
            if (name.trim().length < 3) {
                return {
                    isValid: false,
                    warningMessage: t("validation.documentNameTooShort"),
                };
            }

            // Validate liquid template syntax
            const liquidValidator = new LiquidValidator(t);
            const liquidValidation = liquidValidator.validate(name);
            if (!liquidValidation.isValid) {
                return {
                    isValid: false,
                    errorMessage: liquidValidation.error || t("validation.liquidSyntaxError"),
                };
            }

            return { isValid: true };
        },
        [t],
    );

    const validateDocumentNaming = useCallback((): boolean => {
        return (
            wizardState.outputSettings.documentNaming === "guest_uuid" ||
            (wizardState.outputSettings.documentNaming === "guest_field" &&
                wizardState.outputSettings.customFieldDefinitionId !== null)
        );
    }, [wizardState.outputSettings]);

    const validateFieldMappings = useCallback((): { isValid: boolean; errors: Record<string, FieldValidation> } => {
        const liquidValidator = new LiquidValidator(t);
        const errors: Record<string, FieldValidation> = {};
        let hasErrors = false;

        const validateValue = (key: string, value: string) => {
            if (value.trim() === "") return;
            const result = liquidValidator.validate(value);
            if (!result.isValid) {
                hasErrors = true;
                errors[key] = {
                    isValid: false,
                    errorMessage: result.error || t("validation.liquidSyntaxError"),
                };
            }
        };

        Object.entries(wizardState.fieldMappings).forEach(([fieldName, value]) => validateValue(fieldName, value));
        Object.entries(wizardState.qrCodeOverrides ?? {}).forEach(([id, value]) => validateValue(id, value));

        return { isValid: !hasErrors, errors };
    }, [wizardState.fieldMappings, wizardState.qrCodeOverrides, t]);

    // Single source of truth for step validation
    const validateCurrentStep = useCallback(
        (step: number): boolean => {
            switch (step) {
                case WIZARD_STEPS.SELECT_EVENT:
                    return wizardState.selectedEvent !== null &&
                        wizardState.eventGuestStatistic !== null &&
                        wizardState.eventGuestStatistic.all.count !== 0;
                case WIZARD_STEPS.SELECT_TEMPLATE:
                    return wizardState.selectedTemplate !== null;
                case WIZARD_STEPS.MAP_FIELDS:
                    return validateFieldMappings().isValid;
                case WIZARD_STEPS.OUTPUT_SETTINGS:
                    return (
                        validateDocumentName(wizardState.outputSettings.documentName).isValid &&
                        validateCustomField(
                            wizardState.outputSettings.documentNaming,
                            wizardState.outputSettings.customFieldDefinitionId,
                        ).isValid
                    );
                case WIZARD_STEPS.REVIEW:
                    return true; // Final validation should be done by service
                default:
                    return false;
            }
        },
        [wizardState, validateDocumentName, validateCustomField],
    );

    const triggerValidation = useCallback(
        (step: number) => {
            updateWizardState({
                validationTriggered: { ...wizardState.validationTriggered, [step]: true },
            });
        },
        [updateWizardState, wizardState.validationTriggered],
    );

    const clearValidation = useCallback(
        (step: number) => {
            const newValidationTriggered = { ...wizardState.validationTriggered };
            delete newValidationTriggered[step];
            updateWizardState({
                validationTriggered: newValidationTriggered,
            });
        },
        [updateWizardState, wizardState.validationTriggered],
    );

    const nextStep = useCallback((): { success: boolean; message?: string } => {
        const currentStepValid = validateCurrentStep(wizardState.currentStep);

        if (!currentStepValid) {
            // Trigger validation to show errors
            triggerValidation(wizardState.currentStep);
            return {
                success: false,
                message: t("validation.validationFailed"),
            };
        }

        if (wizardState.currentStep < WIZARD_STEPS.REVIEW) {
            // Clear validation for current step and proceed
            clearValidation(wizardState.currentStep);
            updateWizardState({
                currentStep: wizardState.currentStep + 1,
            });
        }

        return { success: true };
    }, [wizardState.currentStep, updateWizardState, validateCurrentStep, triggerValidation, clearValidation]);

    const previousStep = useCallback(() => {
        if (wizardState.currentStep > WIZARD_STEPS.SELECT_EVENT) {
            updateWizardState({
                currentStep: wizardState.currentStep - 1,
            });
        }
    }, [wizardState.currentStep, updateWizardState]);

    // Check if user can navigate to a step (based on prerequisites)
    const canNavigateToStep = useCallback(
        (step: number): boolean => {
            switch (step) {
                case WIZARD_STEPS.SELECT_EVENT:
                    return true;
                case WIZARD_STEPS.SELECT_TEMPLATE:
                    return wizardState.selectedEvent !== null;
                case WIZARD_STEPS.MAP_FIELDS:
                    return wizardState.selectedEvent !== null && wizardState.selectedTemplate !== null;
                case WIZARD_STEPS.OUTPUT_SETTINGS:
                    return wizardState.selectedEvent !== null && wizardState.selectedTemplate !== null;
                case WIZARD_STEPS.REVIEW:
                    return (
                        wizardState.selectedEvent !== null &&
                        wizardState.selectedTemplate !== null &&
                        validateDocumentNaming()
                    );
                default:
                    return false;
            }
        },
        [wizardState, validateDocumentNaming],
    );

    const getStepValidation = useCallback(
        (step: number): StepValidation => {
            const isTriggered = wizardState.validationTriggered[step] || false;

            switch (step) {
                case WIZARD_STEPS.SELECT_EVENT:
                    return {
                        canNavigateTo: canNavigateToStep(step),
                        canProceedFrom: validateCurrentStep(step),
                        fieldValidations:
                            isTriggered && !validateCurrentStep(step)
                                ? {
                                      selectedEvent: {
                                          isValid: false,
                                          errorMessage: t("validation.missingEvent"),
                                      },
                                  }
                                : {},
                    };
                case WIZARD_STEPS.SELECT_TEMPLATE:
                    return {
                        canNavigateTo: canNavigateToStep(step),
                        canProceedFrom: validateCurrentStep(step),
                        fieldValidations:
                            isTriggered && !validateCurrentStep(step)
                                ? {
                                      selectedTemplate: {
                                          isValid: false,
                                          errorMessage: t("validation.missingTemplate"),
                                      },
                                  }
                                : {},
                    };
                case WIZARD_STEPS.MAP_FIELDS:
                    const fieldMappingsValidation = validateFieldMappings();
                    return {
                        canNavigateTo: canNavigateToStep(step),
                        canProceedFrom: fieldMappingsValidation.isValid,
                        fieldValidations: isTriggered ? fieldMappingsValidation.errors : {},
                    };
                case WIZARD_STEPS.OUTPUT_SETTINGS:
                    const documentNameValidation = validateDocumentName(wizardState.outputSettings.documentName);
                    const customFieldValidation = validateCustomField(
                        wizardState.outputSettings.documentNaming,
                        wizardState.outputSettings.customFieldDefinitionId,
                    );

                    return {
                        canNavigateTo: canNavigateToStep(step),
                        canProceedFrom: validateCurrentStep(step),
                        fieldValidations: isTriggered
                            ? {
                                  documentName: documentNameValidation,
                                  customFieldDefinitionId: customFieldValidation,
                              }
                            : {},
                    };
                case WIZARD_STEPS.REVIEW:
                    return {
                        canNavigateTo: canNavigateToStep(step),
                        canProceedFrom: validateCurrentStep(step),
                        fieldValidations: {},
                    };
                default:
                    return {
                        canNavigateTo: false,
                        canProceedFrom: false,
                        fieldValidations: {},
                    };
            }
        },
        [
            wizardState,
            validateDocumentName,
            validateCustomField,
            validateFieldMappings,
            canNavigateToStep,
            validateCurrentStep,
            t,
        ],
    );

    const resetWizard = useCallback(() => {
        setWizardState(buildEmptyWizardState());
    }, []);

    return {
        wizardState,
        setSelectedEvent,
        setSelectedTemplate,
        setFieldMappings,
        setQrCodeOverrides,
        setOutputSettings,
        setLiquidVariables,
        setEventGuestStatistic,
        goToStep,
        nextStep,
        previousStep,
        canNavigateToStep,
        getStepValidation,
        validateCurrentStep,
        triggerValidation,
        clearValidation,
        resetWizard,
    };
};
